import { useState } from 'react';

import { ExpandLess, ExpandMore } from '@mui/icons-material';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import {
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Typography,
} from '@mui/material';

import { IMAGE_MANIFEST_RBL_TITLE } from '../helpers/helpers';

type InformationListItemProps = {
  informationList: {
    name: string;
    value?: string;
    description?: string;
    shorten?: boolean;
    copyable?: boolean;
    display?: React.ReactNode;
  }[];
  title: string;
};

export const shouldCollapse = (title: string) =>
  title.includes(IMAGE_MANIFEST_RBL_TITLE);

export const InformationListItem = ({
  informationList,
  title,
}: InformationListItemProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const copyAllInfo = () =>
    informationList.reduce(
      (prev, cur) => `${prev}${cur.name}: ${cur.value}\n`,
      '',
    );

  const renderListItems = () => {
    return informationList.map((info, index) => {
      if (!info.value) return null;

      let displayValue = info.value;
      if (info.shorten) {
        displayValue = `${displayValue.substring(0, 50)}...`;
      }

      return (
        <ListItem key={index} dense>
          <ListItemText
            primary={
              <Typography variant="body1">
                {info.name}{' '}
                {info.copyable && (
                  <IconButton
                    onClick={() =>
                      navigator.clipboard.writeText(info.value ?? '')
                    }
                    size="small"
                  >
                    <FileCopyIcon fontSize="inherit" color="action" />
                  </IconButton>
                )}
              </Typography>
            }
            secondary={info.display ?? displayValue}
          />
        </ListItem>
      );
    });
  };

  return (
    <List
      subheader={
        <ListSubheader component="div" disableSticky>
          {title}
          <IconButton
            onClick={() => navigator.clipboard.writeText(copyAllInfo())}
            size="small"
          >
            <FileCopyIcon fontSize="inherit" color="primary" />
          </IconButton>
        </ListSubheader>
      }
    >
      {shouldCollapse(title) ? (
        <>
          <ListItem
            onClick={() => setIsOpen(!isOpen)}
            sx={{ cursor: 'pointer' }}
          >
            <ListItemText primary={isOpen ? 'Hide details' : 'Show details'} />
            {isOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItem>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            {renderListItems()}
          </Collapse>
        </>
      ) : (
        renderListItems()
      )}
    </List>
  );
};
